import React, { Component } from 'react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import Button from 'react-bootstrap/Button'
import axios from 'axios'
import ClipLoader from 'react-spinners/ClipLoader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faVolumeUp, faTimes, faStar } from '@fortawesome/free-solid-svg-icons'
import styles from './Inspector.css'
import 'react-tabs/style/react-tabs.css'

const NUM_TABS = 4

class Inspector extends Component {
  constructor(props) {
    super(props)

    this.audioContext = null
    this.state = {
      selectedWord: null,
      enDictRequested: false,
      jaDictRequested: false,
      jpTranslateRequested: false,
      jpTranslates: null,
      jpError: null,
      enMeanings: null,
      jaMeanings: null,
      enAlternative: null,
      jaAlternative: null,
      pronunciation: null,
      enError: null,
      jaError: null,
      nextWord: null,
      currentDict: 0,
      additionalLinks: null,
      additionalLinksRequested: false
    }
  }

  componentDidMount() {
    const AudioContext = window.AudioContext || window.webkitAudioContext
    this.audioContext = new AudioContext()

    setTimeout(() => this.requestDict(), 10)
  }

  componentDidUpdate(prevProps) {
    if (!this.hasChanges(this.props.words, prevProps.words)) {
      if (this.isDictRequested()) {
        return
      }
      setTimeout(() => this.requestDict(), 10)
      return
    }
    setTimeout(
      () =>
        this.setState(
          {
            selectedWord: null,
            enDictRequested: false,
            jaDictRequested: false,
            jpTranslateRequested: false,
            jpTranslates: null,
            jpError: null,
            enMeanings: null,
            jaMeanings: null,
            enAlternative: null,
            jaAlternative: null,
            pronunciation: null,
            enError: null,
            jaError: null,
            nextWord: null,
            currentDict: 0,
            additionalLinks: null,
            additionalLinksRequested: false
          },
          () => this.requestDict()
        ),
      10
    )
  }

  hasChanges(row1, row2) {
    if (row1.length !== row2.length) {
      return true
    }
    return row1.some((elem, index) => elem !== row2[index])
  }

  copyState() {
    const state = Object.assign({}, this.state)
    if (state.enMeanings !== null) {
      state.enMeanings = state.enMeanings.map((m) => Object.assign({}, m))
    }
    if (state.jaMeanings !== null) {
      state.jaMeanings = state.jaMeanings.map((m) => Object.assign({}, m))
    }
    return state
  }

  parseText(def) {
    const lexpattern = /^([^a-zA-Z]*)([a-zA-Z]+)(.*)/
    let remain = def
    const tokens = []
    while (remain.length > 0) {
      const m = remain.match(lexpattern)
      if (!m) {
        tokens.push(remain)
        break
      }
      if (m[1].length > 0) {
        tokens.push(m[1])
      }
      tokens.push(m[2])
      remain = m[3]
    }
    return tokens
  }

  resolveTranslateRequest(words) {
    if (this.props.translateRequest) {
      return this.props.translateRequest(words)
    }
    return axios.post(`${this.props.baseURL || ''}/translate`, {
      words: words.join(' ')
    })
  }

  resolveWordRequest(word) {
    if (this.props.wordRequest) {
      return this.props.wordRequest(word.toLowerCase())
    }
    return axios.get(`${this.props.baseURL || ''}/word/${word.toLowerCase()}`)
  }

  resolveJaWordRequest(word) {
    if (this.props.jaWordRequest) {
      return this.props.jaWordRequest(word.toLowerCase())
    }
    return axios.get(`${this.props.baseURL || ''}/jaword/${word.toLowerCase()}`)
  }

  async translateWords(words) {
    const resp = await this.resolveTranslateRequest(words)
    console.log('Post', resp)
    if (resp.status !== 200) {
      throw new Error(`Invalid status code: ${resp.status}`)
    }
    return resp.data
  }

  async loadWord(word) {
    const resp = await this.resolveWordRequest(word)
    console.log('Get', resp)
    if (resp.status !== 200) {
      throw new Error(`Invalid status code: ${resp.status}`)
    }
    if (resp.data.results.length === 0) {
      return { word, meanings: [], pronunciation: null, alternative: null }
    }
    const meanings = []
    let pronunciation = null
    const alternative = resp.data.alternative ? resp.data.id : null
    resp.data.results.forEach((result) => {
      result.lexicalEntries.forEach((lexicalEntry) => {
        const pronunciations = (lexicalEntry.pronunciations || []).filter(
          (p) => p.audioFile
        )
        if (pronunciations.length > 0) {
          pronunciation = {
            phoneticSpelling: pronunciations[0].phoneticSpelling,
            audioFile: pronunciations[0].audioFile
          }
        }
        lexicalEntry.entries.forEach((entry) => {
          entry.senses.forEach((sense) => {
            ;(sense.definitions || []).forEach((def) => {
              meanings.push({
                lexicalCategory:
                  (lexicalEntry.lexicalCategory &&
                    lexicalEntry.lexicalCategory.text) ||
                  'unknown',
                text: this.parseText(def)
              })
            })
          })
        })
      })
    })
    return { meanings, pronunciation, alternative }
  }

  async loadJaWord(word) {
    const resp = await this.resolveJaWordRequest(word)
    console.log('Get', resp)
    if (resp.status !== 200) {
      throw new Error(`Invalid status code: ${resp.status}`)
    }
    if (resp.data.results.length === 0) {
      return { word, meanings: [], alternative: null }
    }
    const alternative = resp.data.alternative ? resp.data.id : null
    const meanings = resp.data.results
    return { word, meanings, alternative }
  }

  wordClick(word, index, meaningIndex) {
    console.log('Word', word)
    const state = this.copyState()
    state.nextWord = word
    state.enMeanings.forEach((meaning, mindex) => {
      meaning.selectedWord = mindex === meaningIndex ? index : null
    })
    this.setState(state)
    if (this.props.onWordsOpen) {
      this.props.onWordsOpen([word])
    }
  }

  jaWordClick(word, sindex, tindex, meaningIndex) {
    console.log('Word', word)
    const state = this.copyState()
    state.nextWord = word
    state.jaMeanings.forEach((meaning, mindex) => {
      meaning.selectedWord = mindex === meaningIndex ? [sindex, tindex] : null
    })
    this.setState(state)
    if (this.props.onWordsOpen) {
      this.props.onWordsOpen([word])
    }
  }

  volumeClick(pronunciation) {
    this.play(pronunciation.audioFile)
  }

  async play(url) {
    const source = this.audioContext.createBufferSource()
    source.buffer = await this.loadInternal(url)
    source.connect(this.audioContext.destination)
    source.start(0)
  }

  loadInternal(url) {
    const self = this
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest()
      request.open('GET', url, true)
      request.responseType = 'arraybuffer'

      request.onload = () => {
        self.audioContext.decodeAudioData(
          request.response,
          (buffer) => {
            resolve(buffer)
          },
          () => {
            resolve(null)
            console.warn('Cannot load', url)
          }
        )
      }
      request.send()
    })
  }

  getActualWords() {
    return this.props.words.map((w) => w.split('|')[0])
  }

  isDictRequested() {
    const {
      currentDict,
      enDictRequested,
      jaDictRequested,
      jpTranslateRequested,
      additionalLinksRequested
    } = this.state
    if (currentDict === 0 && !additionalLinksRequested) {
      return false
    }
    if (currentDict === 1 && !jaDictRequested) {
      return false
    }
    if (currentDict === 2 && !enDictRequested) {
      return false
    }
    if (currentDict === 3 && !jpTranslateRequested) {
      return false
    }
    return true
  }

  requestDict() {
    const {
      currentDict,
      enDictRequested,
      jaDictRequested,
      jpTranslateRequested,
      additionalLinksRequested
    } = this.state
    if (currentDict === 0 && !additionalLinksRequested) {
      this.requestAdditionalLinks()
      return
    }
    if (currentDict === 1 && !jaDictRequested) {
      this.requestJaDict()
      return
    }
    if (currentDict === 2 && !enDictRequested) {
      this.requestEnDict()
      return
    }
    if (currentDict === 3 && !jpTranslateRequested) {
      this.requestJpTranslate()
    }
  }

  requestAdditionalLinks() {
    const defaultTabIndex = this.props.words.length > 1 ? NUM_TABS - 1 : 1
    const { onAdditionalLinksRequest } = this.props
    if (!onAdditionalLinksRequest) {
      this.setState(
        {
          additionalLinksRequested: true,
          currentDict: defaultTabIndex
        },
        () => {
          this.requestDict()
        }
      )
      return
    }
    this.setState(
      {
        additionalLinksRequested: true
      },
      () => {
        onAdditionalLinksRequest(this.props.words, (links) => {
          if (!links || links.length === 0) {
            this.setState(
              {
                additionalLinks: [],
                currentDict: defaultTabIndex
              },
              () => this.requestDict()
            )
            return
          }
          this.setState({
            additionalLinks: links
          })
        })
      }
    )
  }

  requestEnDict() {
    const state = this.copyState()
    state.enDictRequested = true
    this.setState(state)
    const words = this.getActualWords()
    if (this.props.onEnDictRequest) {
      this.props.onEnDictRequest(words[0])
    }
    this.loadWord(words[0])
      .then((loadedWordStack) => {
        const state = this.copyState()
        console.log('Meaning', this.props.words, loadedWordStack)
        state.enAlternative = loadedWordStack.alternative
        state.pronunciation = loadedWordStack.pronunciation
        state.enMeanings = loadedWordStack.meanings
        this.setState(state)
      })
      .catch((error) => {
        console.error('Dictionary error', error)
        const state = this.copyState()
        state.enError = error.toString()
        this.setState(state)
      })
  }

  requestJaDict() {
    const state = this.copyState()
    state.jaDictRequested = true
    this.setState(state)
    const words = this.getActualWords()
    if (this.props.onJaDictRequest) {
      this.props.onJaDictRequest(words[0])
    }
    this.loadJaWord(words[0])
      .then((loadedWordStack) => {
        const state = this.copyState()
        console.log('Meaning', this.props.words, loadedWordStack)
        state.jaAlternative = loadedWordStack.alternative
        state.jaMeanings = loadedWordStack.meanings
        this.setState(state)
      })
      .catch((error) => {
        console.error('Dictionary error', error)
        const state = this.copyState()
        state.jaError = error.toString()
        this.setState(state)
      })
  }

  requestJpTranslate() {
    const state = this.copyState()
    state.jpTranslateRequested = true
    this.setState(state)
    const words = this.getActualWords()
    if (this.props.onJpTranslateRequest) {
      this.props.onJpTranslateRequest(words)
    }
    this.translateWords(words)
      .then((loadedWordStack) => {
        const state = this.copyState()
        console.log('Meaning', this.props.words, loadedWordStack)
        state.jpTranslates = loadedWordStack.meanings
        this.setState(state)
      })
      .catch((error) => {
        console.error('Dictionary error', error)
        const state = this.copyState()
        state.jpError = error.toString()
        this.setState(state)
      })
  }

  openAdditionalLink(link) {
    window.open(link.href, '_blank')
  }

  renderLoading() {
    return (
      <div>
        Loading...
        <ClipLoader size='14' />
      </div>
    )
  }

  renderAdditionalLinks() {
    return (
      <div>
        {(this.state.additionalLinks || []).map((link, linkIndex) => (
          <Button
            key={`additional-${link.href}-${linkIndex}`}
            className={styles.AdditionalLink}
            onClick={(e) => this.openAdditionalLink(link)}
            variant='success'
            size='sm'
          >
            {link.text}
            {link.recommended && (
              <span>
                &nbsp;
                <FontAwesomeIcon icon={faStar} />
              </span>
            )}
          </Button>
        ))}
      </div>
    )
  }

  renderEnDict() {
    if (!this.state.enDictRequested) {
      return (
        this.props.words.length === 1 && (
          <div className={styles.enDict}>
            <Button
              data-test-reader-en-dict
              onClick={(e) => this.requestEnDict()}
              variant='info'
              size='sm'
            >
              Oxford English Dictionaryを検索
            </Button>
          </div>
        )
      )
    }
    return this.state.enMeanings !== null ? (
      <div className={styles.DictMean}>
        {this.state.enAlternative &&
          !this.props.words.some((w) => w.includes('|')) && (
            <div className={styles.Alternative}>
              →{this.state.enAlternative}
            </div>
          )}
        {this.state.enMeanings.length === 0 ? (
          <span className={styles.NoData}>Not found</span>
        ) : (
          this.state.enMeanings.map((meaning, meaningIndex) => (
            <div key={meaningIndex}>
              {meaning.lexicalCategory && (
                <span className={styles.LexicalCategory}>
                  {meaning.lexicalCategory}
                </span>
              )}
              {meaning.text.length === 0 ? (
                <span className={styles.NoData}>Not found</span>
              ) : (
                meaning.text.map((t, index) =>
                  t.match(/^[a-zA-Z]+$/) ? (
                    <span
                      key={index}
                      className={
                        meaning.selectedWord === index
                          ? `${styles.Word} ${styles.WordSelected}`
                          : styles.Word
                      }
                      onClick={(ev) => this.wordClick(t, index, meaningIndex)}
                    >
                      {t}
                    </span>
                  ) : (
                    t
                  )
                )
              )}
            </div>
          ))
        )}
      </div>
    ) : this.state.enError ? (
      <div className={styles.DictError}>{this.state.enError}</div>
    ) : (
      this.renderLoading()
    )
  }

  renderJaDict() {
    if (!this.state.jaDictRequested) {
      return (
        this.props.words.length === 1 && (
          <div className={styles.jaDict}>
            <Button
              data-test-reader-ja-dict
              onClick={(e) => this.requestJaDict()}
              variant='info'
              size='sm'
            >
              日本語WordNetを検索
            </Button>
          </div>
        )
      )
    }
    return this.state.jaMeanings !== null ? (
      <div className={styles.DictMean}>
        {this.state.jaAlternative &&
          !this.props.words.some((w) => w.includes('|')) && (
            <div className={styles.Alternative}>
              →{this.state.jaAlternative}
            </div>
          )}
        {this.state.jaMeanings.length === 0 ? (
          <span className={styles.NoData}>Not found</span>
        ) : (
          this.state.jaMeanings.map((meaning, meaningIndex) => (
            <div key={meaningIndex}>
              {meaning.pos && (
                <span className={styles.LexicalCategory}>{meaning.pos}</span>
              )}
              <span className={styles.Japanese}>{meaning.japanese}</span>
              {meaning.english.map((english, index) => (
                <span key={index}>
                  {index > 0 && ','}
                  <span className={styles.JaEnglish}>
                    {english.split(/\W+/).map((t, tindex) => (
                      <span key={index}>
                        {tindex > 0 && ' '}
                        {t.match(/^[a-zA-Z]+$/) ? (
                          <span
                            key={`${index}-${tindex}`}
                            className={
                              meaning.selectedWord &&
                              meaning.selectedWord[0] === index &&
                              meaning.selectedWord[1] === tindex
                                ? `${styles.Word} ${styles.WordSelected}`
                                : styles.Word
                            }
                            onClick={(ev) =>
                              this.jaWordClick(t, index, tindex, meaningIndex)
                            }
                          >
                            {t}
                          </span>
                        ) : (
                          t
                        )}
                      </span>
                    ))}
                  </span>
                </span>
              ))}
            </div>
          ))
        )}
      </div>
    ) : this.state.jaError ? (
      <div className={styles.DictError}>{this.state.jaError}</div>
    ) : (
      this.renderLoading()
    )
  }

  renderJpTranslate() {
    if (!this.state.jpTranslateRequested) {
      return (
        <div className={styles.jpTranslate}>
          <Button
            data-test-reader-jp-translate
            onClick={(e) => this.requestJpTranslate()}
            variant='info'
            size='sm'
          >
            DeepL翻訳で日本語に翻訳
          </Button>
        </div>
      )
    }
    return this.state.jpTranslates !== null ? (
      <div className={styles.DictMean}>
        {this.state.jpTranslates.length === 0 ? (
          <span className={styles.NoData}>Not found</span>
        ) : (
          this.state.jpTranslates.map((meaning, meaningIndex) => (
            <div key={meaningIndex}>
              {meaning.text.length === 0 ? (
                <span className={styles.NoData}>Not found</span>
              ) : (
                meaning.text.map((t, index) =>
                  t.match(/^[a-zA-Z]+$/) ? (
                    <span
                      key={index}
                      className={
                        meaning.selectedWord === index
                          ? `${styles.Word} ${styles.WordSelected}`
                          : styles.Word
                      }
                    >
                      {t}
                    </span>
                  ) : (
                    t
                  )
                )
              )}
            </div>
          ))
        )}
      </div>
    ) : this.state.jpError ? (
      <div className={styles.DictError}>{this.state.jpError}</div>
    ) : (
      this.renderLoading()
    )
  }

  closeStack() {
    if (!this.props.onClose) {
      return
    }
    this.props.onClose()
  }

  searchByGoogle(words) {
    const word = words.join(' ')
    const url = `https://www.google.com/search?q=${encodeURIComponent(
      word
    )}&hl=en`
    if (!this.props.onGoogleSearch) {
      window.open(url, '_blank')
      return
    }
    this.props.onGoogleSearch(words, () => {
      window.open(url, '_blank')
    })
  }

  googleSearch(word, callback) {
    if (!this.props.onGoogleSearch) {
      callback()
      return
    }
    this.props.onGoogleSearch(word, callback)
  }

  enDictRequest(word) {
    if (!this.props.onEnDictRequest) {
      return
    }
    this.props.onEnDictRequest(word)
  }

  jaDictRequest(word) {
    if (!this.props.onJaDictRequest) {
      return
    }
    this.props.onJaDictRequest(word)
  }

  jpTranslateRequest(words) {
    if (!this.props.onJpTranslateRequest) {
      return
    }
    this.props.onJpTranslateRequest(words)
  }

  wordsOpen(words) {
    if (!this.props.onWordsOpen) {
      return
    }
    this.props.onWordsOpen(words)
  }

  closeNext(callback) {
    const state = this.copyState()
    state.nextWord = null
    if (state.enMeanings) {
      state.enMeanings.forEach((m) => {
        m.selectedWord = null
      })
    }
    if (state.jaMeanings) {
      state.jaMeanings.forEach((m) => {
        m.selectedWord = null
      })
    }
    this.setState(state, callback)
  }

  switchTab(index) {
    this.closeNext(() => {
      this.setState({
        currentDict: index
      })
    })
  }

  render() {
    return (
      <div>
        {this.state.nextWord !== null && (
          <Inspector
            words={[this.state.nextWord]}
            onClose={() => this.closeNext()}
            onEnDictRequest={(w) => this.enDictRequest(w)}
            onJaDictRequest={(w) => this.jaDictRequest(w)}
            onJpTranslateRequest={(w) => this.jpTranslateRequest(w)}
            onGoogleSearch={(word, callback) =>
              this.googleSearch(word, callback)
            }
            onWordsOpen={(words) => this.wordsOpen(words)}
            onAdditionalLinksRequest={this.props.onAdditionalLinksRequest}
            wordRequest={(word) => this.resolveWordRequest(word)}
            jaWordRequest={(word) => this.resolveJaWordRequest(word)}
            translateRequest={(words) => this.resolveTranslateRequest(words)}
          />
        )}
        <div className={styles.Dict}>
          <span className={styles.DictWord}>
            {this.props.words.map((word, index) => (
              <span key={index}>
                {index > 0 && '\u00A0'}
                {word.includes('|') ? (
                  <span className={styles.BlankWord}>
                    {Array.from(word.split('|')[0])
                      .map((c) => '\u00A0')
                      .join('')}
                  </span>
                ) : (
                  <span>{word === 'I' ? word : word.toLowerCase()}</span>
                )}
              </span>
            ))}
            {this.state.pronunciation && (
              <span
                className={styles.Voice}
                onClick={(ev) => this.volumeClick(this.state.pronunciation)}
              >
                <span className={styles.PhoneticSpelling}>
                  {this.state.pronunciation.phoneticSpelling}
                </span>
                <FontAwesomeIcon icon={faVolumeUp} />
              </span>
            )}
            <span className={styles.HintCaption}>のヒント</span>
          </span>
          <div className={styles.CloseButton}>
            <span onClick={(ev) => this.closeStack()}>
              <FontAwesomeIcon icon={faTimes} />
            </span>
          </div>
          {this.props.words.every((w) => !w.includes('|')) && (
            <div className={styles.GoogleSearch}>
              <a
                href='#'
                onClick={(e) => this.searchByGoogle(this.getActualWords())}
              >
                Search by Google
              </a>
            </div>
          )}
          <div className={styles.DictTabs}>
            <Tabs
              selectedIndex={this.state.currentDict}
              onSelect={(index) => this.switchTab(index)}
            >
              <TabList>
                <Tab disabled={(this.state.additionalLinks || []).length === 0}>
                  解説コンテンツ
                </Tab>
                <Tab disabled={this.props.words.length > 1}>英和辞典</Tab>
                <Tab disabled={this.props.words.length > 1}>英英辞典</Tab>
                <Tab>DeepL翻訳</Tab>
              </TabList>
              <TabPanel>{this.renderAdditionalLinks()}</TabPanel>
              <TabPanel>{this.renderJaDict()}</TabPanel>
              <TabPanel>{this.renderEnDict()}</TabPanel>
              <TabPanel>{this.renderJpTranslate()}</TabPanel>
            </Tabs>
          </div>
        </div>
      </div>
    )
  }
}

export default Inspector
